import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { WeeklyCheckIn } from '../Chat/WeeklyCheckIn';
import { ReleaseNotes } from './ReleaseNotes';
import { Resume } from '../Resume/Resume';
import { useNavigate } from 'react-router-dom';
import { getBuildInfo } from '../../buildInfo';
import { getConversations } from '../../services/conversations';

export function Dashboard() {
  const { user, userProfile } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [viewConversationId, setViewConversationId] = useState<string | null>(null);
  const [showResume, setShowResume] = useState(false);
  const [conversationCount, setConversationCount] = useState<number | null>(null);
  const build = getBuildInfo();

  // Fetch conversation count on mount
  useEffect(() => {
    async function fetchConversationCount() {
      try {
        const conversations = await getConversations();
        setConversationCount(conversations.length);
      } catch (err) {
        console.error('Failed to fetch conversation count:', err);
        setConversationCount(0);
      }
    }
    fetchConversationCount();
  }, []);

  const handleBackToDashboard = (options?: { redirectToTimeline?: boolean }) => {
    if (options?.redirectToTimeline) {
      navigate('/career-timeline');
      return;
    }
    setShowCheckIn(false);
    setViewConversationId(null);
    setShowResume(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'transparent',
    }}>
      {/* Main Content */}
      <main style={{ padding: '40px 32px', maxWidth: '1200px', margin: '0 auto' }}>
        {showCheckIn ? (
          <div style={{ minHeight: '600px' }}>
            <button
              onClick={() => handleBackToDashboard()}
              style={{
                marginBottom: '20px',
                padding: '8px 16px',
                backgroundColor: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              ← {t('dashboard.backToDashboard')}
            </button>
            <WeeklyCheckIn
              conversationId={viewConversationId}
              key={viewConversationId || 'new'}
              onEndCheckIn={handleBackToDashboard}
            />
          </div>
        ) : showResume ? (
          <div style={{ minHeight: '600px' }}>
            <button
              onClick={() => handleBackToDashboard()}
              style={{
                marginBottom: '20px',
                padding: '8px 16px',
                backgroundColor: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              ← {t('dashboard.backToDashboard')}
            </button>
            <Resume />
          </div>
        ) : (
          <div style={{
            backgroundColor: 'var(--surface)',
            padding: '32px',
            borderRadius: '8px',
            boxShadow: 'var(--shadow-sm)',
          }}>
            <h2 style={{ marginBottom: '16px' }}>
              {t('dashboard.welcome')}, {userProfile?.displayName || user?.displayName}!
            </h2>

            <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>
              {t('common.language') === 'Language' 
                ? `Your AI career coach ${userProfile?.assistantName || 'Alex'} ${t('dashboard.isReadyToHelp')}`
                : `Votre coach IA ${userProfile?.assistantName || 'Alex'} ${t('dashboard.isReadyToHelp')}`
              }
            </p>

            <div
              data-testid="dashboard-beta-warning"
              style={{
                marginBottom: '24px',
                padding: '16px',
                border: '1px solid var(--border)',
                borderLeft: '4px solid var(--danger)',
                borderRadius: '8px',
                backgroundColor: 'var(--surface-muted)',
              }}
            >
              <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>{t('dashboard.betaWarningTitle')}</h3>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '14px', lineHeight: 1.5 }}>
                {t('dashboard.betaWarningBody')}
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px',
              marginTop: '32px',
            }}>
              <div style={{
                padding: '20px',
                border: '1px solid var(--border)',
                borderRadius: '8px',
              }}>
                <h3 style={{ marginBottom: '8px' }}>
                  {t('dashboard.weeklyCheckIn')}
                  {conversationCount !== null && conversationCount > 0 && (
                    <span style={{
                      marginLeft: '8px',
                      fontSize: '12px',
                      backgroundColor: 'var(--primary)',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontWeight: 'normal',
                    }}>
                      {conversationCount}
                    </span>
                  )}
                </h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                  {t('dashboard.weeklyCheckInDesc')}
                </p>
                <button
                  data-testid="dashboard-manage-checkins"
                  onClick={() => navigate('/weekly-checkin')}
                  style={{
                    marginTop: '16px',
                    padding: '10px 20px',
                    backgroundColor: 'var(--primary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  {t('dashboard.startCheckIn')}
                </button>
              </div>

              <div style={{
                padding: '20px',
                border: '1px solid var(--border)',
                borderRadius: '8px',
              }}>
                <h3 style={{ marginBottom: '8px' }}>{t('dashboard.resume')}</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                  {t('dashboard.resumeDesc')}
                </p>
                <button
                  onClick={() => setShowResume(true)}
                  style={{
                    marginTop: '16px',
                    padding: '10px 20px',
                    backgroundColor: 'var(--surface)',
                    color: 'var(--primary)',
                    border: '1px solid var(--primary)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  {t('dashboard.viewResume')}
                </button>
              </div>

              <div style={{
                padding: '20px',
                border: '1px solid var(--border)',
                borderRadius: '8px',
              }}>
                <h3 style={{ marginBottom: '8px' }}>🎤 {t('dashboard.careerMemory')}</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                  {t('dashboard.careerMemoryDesc')}
                </p>
                <button
                  onClick={() => navigate('/career-timeline')}
                  style={{
                    marginTop: '16px',
                    padding: '10px 20px',
                    backgroundColor: 'var(--surface)',
                    color: 'var(--primary)',
                    border: '1px solid var(--primary)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  {t('dashboard.buildMemory')}
                </button>
              </div>

              <div style={{
                padding: '20px',
                border: '1px solid var(--border)',
                borderRadius: '8px',
              }}>
                <h3 style={{ marginBottom: '8px' }}>{t('dashboard.careerVision')}</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                  {t('dashboard.careerVisionDesc')}
                </p>
                <button
                  onClick={() => navigate('/career-vision')}
                  style={{
                    marginTop: '16px',
                    padding: '10px 20px',
                    backgroundColor: 'var(--surface)',
                    color: 'var(--primary)',
                    border: '1px solid var(--primary)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  {t('dashboard.openVision')}
                </button>
              </div>

              <div style={{
                padding: '20px',
                border: '2px solid var(--primary)',
                borderRadius: '8px',
                backgroundColor: 'var(--surface-muted)',
              }}>
                <h3 data-testid="dashboard-fine-tune-title" style={{ marginBottom: '8px', color: 'var(--primary)' }}>
                  ✨ {t('dashboard.fineTuneTitle')}
                </h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                  {t('dashboard.fineTuneDesc')}
                </p>
                <button
                  onClick={() => navigate('/resume-tuner')}
                  style={{
                    marginTop: '16px',
                    padding: '10px 20px',
                    backgroundColor: 'var(--primary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                  }}
                >
                  {t('dashboard.startTuning')}
                </button>
              </div>

              <div style={{
                padding: '20px',
                border: '1px solid var(--border)',
                borderRadius: '8px',
              }}>
                <h3 style={{ marginBottom: '8px' }}>🧠 {t('graph.title')}</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                  {t('dashboard.experienceGraphDesc') || 'Visualize your career as an interconnected knowledge graph.'}
                </p>
                <button
                  onClick={() => navigate('/experience-graph')}
                  style={{
                    marginTop: '16px',
                    padding: '10px 20px',
                    backgroundColor: 'var(--surface)',
                    color: 'var(--primary)',
                    border: '1px solid var(--primary)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  {t('nav.experienceGraph')}
                </button>
              </div>

              <div style={{
                padding: '20px',
                border: '1px solid var(--border)',
                borderRadius: '8px',
              }}>
                <h3 style={{ marginBottom: '8px' }}>{t('dashboard.linkedinPosts')}</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                  {t('dashboard.linkedinDesc')}
                </p>
                <button
                  data-testid="dashboard-linkedin-create-post"
                  onClick={() => navigate('/linkedin-posts')}
                  style={{
                  marginTop: '16px',
                  padding: '10px 20px',
                  backgroundColor: 'var(--surface)',
                  color: 'var(--primary)',
                  border: '1px solid var(--primary)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
                >
                  {t('dashboard.createPost')}
                </button>
              </div>
            </div>

            <div style={{
              marginTop: '40px',
              padding: '20px',
              backgroundColor: 'var(--surface-muted)',
              borderRadius: '8px',
            }}>
              <h3 style={{ marginBottom: '12px' }}>{t('dashboard.accountInfo')}</h3>
              <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                <p><strong>{t('dashboard.email')}:</strong> {user?.email}</p>
                <p><strong>{t('dashboard.subscription')}:</strong> {userProfile?.subscriptionTier || 'Free'}</p>
                <p>
                  <strong>{t('dashboard.memberSince')}:</strong>{' '}
                  <span data-testid="dashboard-member-since">
                    {userProfile?.createdAt ? userProfile.createdAt.toLocaleDateString() : t('dashboard.today')}
                  </span>
                </p>
              </div>
            </div>

            {/* Release Notes */}
            <ReleaseNotes />
          </div>
        )}
      </main>

      {/* Footer with build info */}
      <footer style={{
        padding: '16px 32px',
        textAlign: 'center',
        color: 'var(--text-muted)',
        fontSize: '12px',
        borderTop: '1px solid var(--border)',
      }}>
        v{build.version} · {build.branch}{build.sha ? `@${build.sha}` : ''}
      </footer>
    </div>
  );
}
